const parseSystemDataValue = (systemData) => {
  if (!systemData) {
    return null;
  }

  if (typeof systemData === "string") {
    try {
      return JSON.parse(systemData);
    } catch (error) {
      console.error("Failed to parse systemData payload", error);
      return null;
    }
  }

  if (typeof systemData === "object") {
    return systemData;
  }

  return null;
};

export const getSystemEnvironments = (systemData) => {
  const parsedSystemData = parseSystemDataValue(systemData);
  if (!parsedSystemData) {
    return [];
  }

  if (
    parsedSystemData.json &&
    typeof parsedSystemData.json === "string" &&
    parsedSystemData.json.trim()
  ) {
    try {
      const embeddedSystemData = JSON.parse(parsedSystemData.json);
      if (
        embeddedSystemData?.environments &&
        typeof embeddedSystemData.environments === "object"
      ) {
        return Object.values(embeddedSystemData.environments);
      }
    } catch (error) {
      console.error("Failed to parse embedded systemData.json payload", error);
    }
  }

  if (
    parsedSystemData.environments &&
    typeof parsedSystemData.environments === "object"
  ) {
    return Object.values(parsedSystemData.environments);
  }

  return [];
};

const normalizeId = (value) => {
  if (value === null || value === undefined) {
    return "";
  }

  return String(value).trim();
};

const getElementIdCandidates = (elementData) => {
  const candidates = [
    elementData?.id,
    elementData?.elementID,
    elementData?.elementId,
    elementData?.elementData?.id,
  ];

  return candidates
    .map(normalizeId)
    .filter(Boolean);
};

const getEnvironmentElementIdCandidates = (environmentElement) => {
  const candidates = [
    environmentElement?.elementID,
    environmentElement?.elementId,
    environmentElement?.id,
    environmentElement?.elementData?.id,
  ];

  return candidates
    .map(normalizeId)
    .filter(Boolean);
};

const getTargetEnvironmentId = (environmentElement) => {
  const targetEnvironmentId =
    environmentElement?.targetEnvironmentId ||
    environmentElement?.targetEnvironmentID ||
    environmentElement?.targetEnvironment?.id ||
    environmentElement?.targetEnvironmentData?.id ||
    environmentElement?.targetEnvironmentDataID ||
    "";

  return normalizeId(targetEnvironmentId);
};

const getEnvironmentIdCandidates = (environment) =>
  [environment?.id, environment?.environmentId, environment?.environmentID]
    .map(normalizeId)
    .filter(Boolean);

const getEnvironmentElements = (environment) => {
  const sources = [
    environment?.elements,
    environment?.elementJsons,
    environment?.elementDatas,
  ];

  for (const source of sources) {
    if (Array.isArray(source) && source.length > 0) {
      return source;
    }

    if (source && typeof source === "object") {
      const values = Object.values(source);
      if (values.length > 0) {
        return values;
      }
    }
  }

  return [];
};

const getDirectParentEnvironmentId = (environment) =>
  normalizeId(
    environment?.parentEnvironmentId ||
      environment?.parentEnvironmentID ||
      environment?.parentEnvironment?.id ||
      environment?.parentEnvironmentData?.id ||
      environment?.parentEnvironmentDataID ||
      environment?.parentId ||
      environment?.parentID ||
      environment?.parent?.id,
  );

export const getSelectedElementTargetEnvironmentId = ({
  systemData,
  currentEnvironmentId,
  elementData,
}) => {
  const selectedElementIds = getElementIdCandidates(elementData);
  if (!selectedElementIds.length) {
    return "";
  }

  const selectedElementIdSet = new Set(selectedElementIds);
  const environments = getSystemEnvironments(systemData);
  const validEnvironmentIds = new Set(
    environments.flatMap((environment) => getEnvironmentIdCandidates(environment)),
  );
  const normalizedCurrentEnvironmentId = normalizeId(currentEnvironmentId);
  const currentEnvironment = environments.find(
    (environment) => {
      const environmentIds = getEnvironmentIdCandidates(environment);
      return environmentIds.includes(normalizedCurrentEnvironmentId);
    },
  );
  const candidateEnvironments = currentEnvironment
    ? [currentEnvironment]
    : environments;

  for (const environment of candidateEnvironments) {
    for (const environmentElement of getEnvironmentElements(environment)) {
      const environmentElementIds =
        getEnvironmentElementIdCandidates(environmentElement);
      const matchesSelectedElement = environmentElementIds.some((id) =>
        selectedElementIdSet.has(id),
      );

      if (matchesSelectedElement) {
        const targetEnvironmentId = getTargetEnvironmentId(environmentElement);
        return validEnvironmentIds.has(targetEnvironmentId)
          ? targetEnvironmentId
          : "";
      }
    }
  }

  return "";
};

export const getParentEnvironmentId = ({
  systemData,
  currentEnvironmentId,
  currentEnvironment,
}) => {
  const normalizedCurrentEnvironmentId = normalizeId(
    currentEnvironmentId || currentEnvironment?.id,
  );
  if (!normalizedCurrentEnvironmentId) {
    return "";
  }

  const environments = getSystemEnvironments(systemData);
  const validEnvironmentIds = new Set(
    environments.flatMap((environment) => getEnvironmentIdCandidates(environment)),
  );
  const matchingSystemEnvironment = environments.find((environment) =>
    getEnvironmentIdCandidates(environment).includes(normalizedCurrentEnvironmentId),
  );
  const directParentEnvironmentId =
    getDirectParentEnvironmentId(currentEnvironment) ||
    getDirectParentEnvironmentId(matchingSystemEnvironment);

  if (
    directParentEnvironmentId &&
    (validEnvironmentIds.size === 0 ||
      validEnvironmentIds.has(directParentEnvironmentId))
  ) {
    return directParentEnvironmentId;
  }

  for (const environment of environments) {
    const sourceEnvironmentId = getEnvironmentIdCandidates(environment).find(Boolean);
    if (!sourceEnvironmentId) {
      continue;
    }

    const hasCurrentEnvironmentTarget = getEnvironmentElements(environment).some(
      (environmentElement) =>
        getTargetEnvironmentId(environmentElement) === normalizedCurrentEnvironmentId,
    );

    if (hasCurrentEnvironmentTarget) {
      return sourceEnvironmentId;
    }
  }

  return "";
};

export const normalizeEnvironmentLoadStatesPayload = (payload) => {
  if (!payload) {
    return null;
  }

  if (typeof payload === "string") {
    const trimmedPayload = payload.trim();
    if (!trimmedPayload) {
      return null;
    }

    try {
      return normalizeEnvironmentLoadStatesPayload(JSON.parse(trimmedPayload));
    } catch (error) {
      console.error("Failed to parse environmentLoadStatesChanged payload", error);
      return null;
    }
  }

  if (typeof payload !== "object") {
    return null;
  }

  const environments = Array.isArray(payload.environments)
    ? payload.environments
    : [];

  return {
    ...payload,
    currentEnvironmentId: normalizeId(payload.currentEnvironmentId),
    environments: environments.filter(
      (environment) => environment && typeof environment === "object",
    ),
  };
};

export const getEnvironmentLoadStateById = (
  environmentLoadStatesPayload,
  environmentId,
) => {
  const normalizedEnvironmentId = normalizeId(environmentId);
  if (!normalizedEnvironmentId) {
    return null;
  }

  const normalizedPayload = normalizeEnvironmentLoadStatesPayload(
    environmentLoadStatesPayload,
  );
  if (!normalizedPayload) {
    return null;
  }

  return (
    normalizedPayload.environments.find(
      (environment) => normalizeId(environment.id) === normalizedEnvironmentId,
    ) || null
  );
};

export const canEnterEnvironment = (
  environmentLoadStatesPayload,
  environmentId,
) =>
  getEnvironmentLoadStateById(environmentLoadStatesPayload, environmentId)
    ?.canEnter === true;
